<template>
  <Form
    ref="formRef"
    :rules="formRules"
    :model="state"
    :layout="layout"
  >
    <div
      class="select-with-numeric-interval"
      :class="`select-with-numeric-interval_${layout}`"
    >
      <FormItem
        name="type"
        class="select-with-numeric-interval__item"
        :class="`select-with-numeric-interval__item_${layout}`"
        :label="selectLabel"
      >
        <Select
          :value="state.type || undefined"
          :placeholder="placeholder"
          :allowClear="allowClear"
          :showSearch="showSearch"
          :filterOption="filterOption"
          @change="handleSelectChange"
          @search="$emit('search', $event)"
        >
          <SelectOption
            v-if="showAnyTypeOption"
            :value="anyTypeOption.id"
            @click.stop
          >
            {{ anyTypeOption.name }}
          </SelectOption>
          <SelectOption
            v-for="option in options"
            :key="option.id"
            :value="option.id"
            :disabled="option.meta?.disabled"
            @click.stop
          >
            <slot
              name="optionViewTemplate"
              :option="option"
            >
              {{ option.name }}
            </slot>
          </SelectOption>
        </Select>
      </FormItem>

      <FormItem
        v-if="showPackagingQuantity"
        name="quantity"
        class="select-with-numeric-interval__item"
        :class="`select-with-numeric-interval__item_${layout}`"
        :label="quantityLabel"
      >
        <CustomNumericInterval
          :initialValueFrom="state.quantity.from"
          :initialValueTo="state.quantity.to"
          :error="errors"
          :mode="mode"
          :placeholder="singleValuePlaceholder"
          placeholderFrom="0"
          placeholderTo="∞"
          @change="handleIntervalChange"
        />
      </FormItem>
    </div>
  </Form>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  PropType,
  computed,
  watch,
  reactive,
  onMounted,
} from 'vue';
import {
  Form,
  FormItem,
  Select,
  SelectOption,
} from 'ant-design-vue';
import { SelectValue } from 'ant-design-vue/lib/select';

import { TEntity } from '@/types';
import { ANY_TYPE_OPTION, ELayout } from '@/constants';
import { useFormHandlers } from '@/composables/useFormHandlers';
import { ENumericIntervalMode } from '@/ui/experimental/CustomNumericInterval/constants';
import tt from '@/i18n/utils/translateText';

import CustomNumericInterval from '../CustomNumericInterval/index.vue';

export default defineComponent({
  name: 'SelectWithNumericInterval',
  components: {
    CustomNumericInterval,
    Select,
    SelectOption,
    Form,
    FormItem,
  },
  props: {
    initialValueType: {
      type: [String, null] as PropType<string | null>,
      default: null,
    },
    initialValueFrom: {
      type: [Number, null] as PropType<number | null>,
      default: null,
    },
    initialValueTo: {
      type: [Number, null] as PropType<number | null>,
      default: null,
    },
    mode: {
      type: String as PropType<ENumericIntervalMode>,
      default: ENumericIntervalMode.singleAndInterval,
    },
    placeholder: {
      type: String,
      default: '',
    },
    singleValuePlaceholder: {
      type: String,
      default: tt('shared.anyAmount'),
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    options: {
      type: Array as PropType<(TEntity & { meta?: { withQuantity: boolean, disabled?: boolean } })[]>,
      default: () => [],
    },
    showAnyTypeOption: {
      type: Boolean,
      default: false,
    },
    allowClear: {
      type: Boolean,
      default: false,
    },
    showSearch: {
      type: Boolean,
      default: false,
    },
    filterOption: {
      type: Boolean,
      default: false,
    },
    selectLabel: {
      type: String,
      default: '',
    },
    quantityLabel: {
      type: String,
      default: '',
    },
    layout: {
      type: String as PropType<ELayout>,
      default: ELayout.horizontal,
    },
  },
  emits: ['change', 'search'],
  setup(props, { emit, expose }) {
    const formRef = ref<typeof Form | null>(null);
    const anyTypeOption = ANY_TYPE_OPTION;

    const rules = {
      type: [],
      quantity: [],
    };

    const { formRules, onControlChange, onError } = useFormHandlers(formRef, { rules });

    const setError = () => {
      onError({ errors: props.errors });
    };

    watch(() => props.errors, setError);

    const state = reactive({
      type: props.initialValueType,
      quantity: {
        from: props.initialValueFrom,
        to: props.initialValueTo,
      },
    });

    const setValueFromProps = () => {
      state.type = props.initialValueType;
      state.quantity = {
        from: props.initialValueFrom,
        to: props.initialValueTo,
      };
    };

    watch(() => [props.initialValueType, props.initialValueFrom, props.initialValueTo], setValueFromProps);

    const onValidate = () => {
      formRef.value?.validate().catch(() => {});
    };

    expose({ validate: onValidate });

    const handleIntervalChange = (changedValue: { from: number | null, to: number | null }) => {
      state.quantity = changedValue;
      onControlChange({ name: 'quantity' });
      emit('change', state);
    };

    // TODO При замене Antd Select на компонент из UI изменить тип `SelectValue` -> `string` (если будет актуально)
    const handleSelectChange = (selectedType: SelectValue) => {
      const selectedTypeInOptions = props.options.find(
        (option) => option.id === selectedType);
      const selectedTypeHasQuantity = !!selectedTypeInOptions?.meta?.withQuantity;

      state.type = selectedType as string;
      state.quantity = selectedTypeHasQuantity ? state.quantity : {
        from: null,
        to: null,
      };
      onControlChange({ name: 'type' });
      emit('change', state);
    };

    const showPackagingQuantity = computed(() => {
      const currentTypeInOptions = props.options.find((option) => option.id === state.type);
      if (!currentTypeInOptions) return false;
      return currentTypeInOptions.meta?.withQuantity;
    });

    onMounted(setError);

    return {
      state,
      formRules,
      formRef,
      anyTypeOption,
      showPackagingQuantity,

      handleIntervalChange,
      handleSelectChange,
    };
  },
});
</script>

<style lang="scss" src="./styles.scss" />
